import React, { useEffect, useCallback } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
    Button, Switch, Table, Row, Tag, Typography, Col,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import {
    selectLocationsPageDataRequest, selectLoadingActiveStatus,
} from '../../../../state/ui/configuration/location/locationUIReducer';
import { selectLocations } from '../../../../state/domain/locations/locationReducer';
import {
    editLocation as editLocationAction,
    requestLocationPageData, requestPublishLocation,
} from '../../../../state/ui/configuration/location/locationUiActions';
import EditLocation from '../location/EditLocation';
import { filterPublished } from '../inspection/config-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from '../company/user-util';

const { Column } = Table;
const { Text } = Typography;


// DESCRIPTIONS FOR CODES
const InheritedNameDescription = ({ text, userGroupText}) => {
    let inherited = false;
    let name =  text != null ? text : null;
    if (name == null) {
        name = userGroupText || '';
        inherited = true;
    }
    return (
        <Row type="flex" align="middle" justify="space-between">
            <Col style={{ width: 'calc(100% - 120px)' }}>
                <Text>{name}</Text>
            </Col>
            <Col style={{ width: '120px' }}>
                {inherited
                    ? <Tag color="blue" style={{ marginLeft: '0.5em' }}><FormattedMessage id="contactDetails" /></Tag>
                    : <></>
                }
            </Col>
        </Row>
    );
};
InheritedNameDescription.propTypes = {
    text: PropTypes.string,
    userGroupText:  PropTypes.string.isRequired,
};

InheritedNameDescription.defaultProps = {
    text: '',
    userGroupText: '',
};


// TABLE
function LocationsTable({
                          isLoading, locations, onActiveUpdate, onEditButton, loadingActive, sessionUser,
                      }) {
    const isActiveLoading = useCallback(location => !!loadingActive.find(code => `${code}` === `${location.locationCode}`), [loadingActive]);

    const RowTotal = () => (
        <div>
            <Text>{`${locations.length} `}</Text>
            <FormattedMessage id="items" />
        </div>
    );

    // EXTRA FILTERS
    //none

    // SORTS
    function sortLocationsByDesc(a, b) {
        const aStr = `${a.locationDescription}`;
        const bStr = `${b.locationDescription}`;
        return aStr.localeCompare(bStr);
    }
    function sortLocationsByTrading(a, b) {
        const aStr = `${a.tradingName}` || '';
        const bStr = `${b.tradingName}` || '';
        return aStr.localeCompare(bStr);
    }
    function sortLocationsByEmail(a, b) {
        const aStr = `${a.emailContact}` || '';
        const bStr = `${a.emailContact}` || '';
        return aStr.localeCompare(bStr);
    }

    // TABLE SETTINGS AND COLUMNS
    return (
        <Table
            pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
            scroll={{ x: 1, y: false }}
            loading={isLoading}
            dataSource={locations}
            rowKey={location => location.locationCode}
        >
            <Column
                title={<FormattedMessage id="published" />}
                dataIndex="active"
                key="active"
                width={110}
                filtered
                filters={[{ text: <FormattedMessage id="published" />, value: true }, { text: <FormattedMessage id="notPublished" />, value: false }]}
                filteredValue={locations.active}
                onFilter={filterPublished}
                render={(active, record) => (
                    <Row type="flex" justify="center">
                        {userIsAdmin(sessionUser)
                            ? <Switch loading={isActiveLoading(record)} onChange={() => onActiveUpdate(record)} size="small" checked={active} />
                            : <Tag color={active ? 'blue' : 'volcano'}><FormattedMessage id={active ? 'published' : 'notPublished'} /></Tag> }
                    </Row>
                )}
            />
            <Column
                sorter={sortLocationsByDesc}
                defaultSortOrder="ascend"
                title={<FormattedMessage id="locationName" />}
                width="20%"
                dataIndex="locationDescription"
                key="locationDescription"
            />
            <Column
                sorter={sortLocationsByTrading}
                title={<FormattedMessage id="tradingName" />}
                width="30%"
                dataIndex="tradingName"
                key="tradingName"
                render={(tradingName, location) => (
                    <InheritedNameDescription
                        text={tradingName}
                        userGroupText={location.companyName}
                    />)}
            />
            <Column
                sorter={sortLocationsByEmail}
                title={<FormattedMessage id="email" />}
                width="30%"
                dataIndex="contactEmail"
                key="contactEmail"
                render={(contactEmail, location) => (
                    <InheritedNameDescription
                        text={contactEmail}
                        userGroupText={location.companyEmail}
                    />)}
            />
            {userIsAdmin(sessionUser)
                ? (
                    <Column
                        className="via-action-column"
                        align="center"
                        width={70}
                        render={(text, record) => (
                            <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                                <FormattedMessage id="edit" />
                            </Button>
                        )}
                    />
                )
                : ''
            }
        </Table>
    );
}

LocationsTable.propTypes = {
    isLoading: PropTypes.bool,
    onActiveUpdate: PropTypes.func.isRequired,
    onEditButton: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            locationCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            locationDescription: PropTypes.string.isRequired,
            tradingName: PropTypes.string,
            emailContact: PropTypes.string,
            addressCode: PropTypes.number,
            addressDetails: PropTypes.shape({
                line1: PropTypes.string,
                line2: PropTypes.string,
                line3: PropTypes.string,
                line4: PropTypes.string,
                region: PropTypes.string,
                zipPostcode: PropTypes.string,
                country: PropTypes.string,
            }),
            logoData: PropTypes.string,
            logoType: PropTypes.string,
            active: PropTypes.bool.isRequired,
            companyName: PropTypes.string,
            companyEmail: PropTypes.string,
        }),
    ),
    loadingActive: PropTypes.arrayOf(PropTypes.string),
    sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
    userGroup: PropTypes.object.isRequired,
};

LocationsTable.defaultProps = {
    isLoading: false,
    locations: [],
    loadingActive: [],
    userGroup: {}
};

function Locations({isLoading, loadPageData, locations, loadingActive,
                    editLocation, updateActiveStatus, sessionUser,}) {
    // Load locations on first entry - these only need to be loaded once.
    useEffect(() => {
        loadPageData();
    }, [loadPageData]);

    // Extra validation helper
    const doesDescriptionExist = (code, value) => {
        const matchedLocation = locations.find(row => row.locationDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());
        return matchedLocation ? matchedLocation.locationCode !== code : false;
    };

    const companyName = locations && locations.length > 0 ? locations[0].companyName : null;
    const companyEmail = locations && locations.length > 0 ? locations[0].companyEmail : null;

    // TITLE ROW
    const CreateButton = () => (
        <Button type="primary" onClick={() => editLocation(null)} loading={isLoading} icon="plus">
            <FormattedMessage id="create" />
        </Button>
    );

    // MAIN COMPONENT
    return (
        <ConfigHeading
            titleId="locations"
            descriptionId="locationsDesc"
            actions={(
                userIsAdmin(sessionUser)
                    ? <CreateButton />
                    : <></>
            )}
        >
            <LocationsTable
                isLoading={isLoading}
                locations={locations}
                loadingActive={loadingActive}
                onActiveUpdate={updateActiveStatus}
                onEditButton={editLocation}
                sessionUser={sessionUser}
            />
            <EditLocation descValidator={doesDescriptionExist} companyName={companyName} companyEmail={companyEmail} />
        </ConfigHeading>
    );
}

// Validate the prop type parameters.
Locations.propTypes = {
    isLoading: PropTypes.bool,
    loadPageData: PropTypes.func.isRequired,
    editLocation: PropTypes.func.isRequired,
    updateActiveStatus: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            locationCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            locationDescription: PropTypes.string.isRequired,
            tradingName: PropTypes.string,
            emailContact: PropTypes.string,
            addressCode: PropTypes.number,
            addressDetails: PropTypes.shape({
                line1: PropTypes.string,
                line2: PropTypes.string,
                line3: PropTypes.string,
                line4: PropTypes.string,
                region: PropTypes.string,
                zipPostcode: PropTypes.string,
                country: PropTypes.string,
            }),
            logoData: PropTypes.string,
            logoType: PropTypes.string,
            active: PropTypes.bool.isRequired,
            companyName: PropTypes.string,
            companyEmail: PropTypes.string,
        }),
    ),
    loadingActive: PropTypes.arrayOf(PropTypes.string),
    sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
};

Locations.defaultProps = {
    isLoading: false,
    locations: [],
    loadingActive: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
    const pageDataRequest = selectLocationsPageDataRequest(state);
    const Locations = selectLocations(state);

    return {
        isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
        error: pageDataRequest ? pageDataRequest.error : null,
        locations: Locations,
        loadingActive: selectLoadingActiveStatus(state),
        sessionUser: selectSessionUser(state),
    };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
    loadPageData: () => dispatch(requestLocationPageData()),
    // eslint-disable-next-line max-len
    editLocation: location => dispatch(editLocationAction(location ? location.locationCode : null)),
    updateActiveStatus: location => dispatch(requestPublishLocation(location)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(Locations);
